import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import closeIcon from "../../assets/cross.svg";

// School-admin edit of a roster student's basic details (name + grade).
// Email is the Clerk-linked login identity, so it's shown read-only here —
// the backend endpoint only accepts firstName / lastName / grade.
const EditStudentModal = ({ student, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(student?.firstName || "");
  const [lastName, setLastName] = useState(student?.lastName || "");
  const [grade, setGrade] = useState(student?.grade || "");
  // True while the save is in flight — disables the buttons and shows a
  // spinner. The parent closes the modal on success, so we don't reset here.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    if (isSubmitting) return;
    // Only send the fields that actually changed, trimmed. Empty string is a
    // valid value (clears the field), so we compare against the original.
    const payload = {};
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedGrade = grade.trim();
    if (trimmedFirst !== (student?.firstName || "")) payload.firstName = trimmedFirst;
    if (trimmedLast !== (student?.lastName || "")) payload.lastName = trimmedLast;
    if (trimmedGrade !== (student?.grade || "")) payload.grade = trimmedGrade;

    // Nothing changed — just close without a round-trip.
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      await onConfirm(student, payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "h-11 px-3.5 bg-white rounded-[10px] border border-gray-300 text-gray-900 text-base font-medium outline-none focus:border-blue-500 transition-colors";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[560px] bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        {/* Header */}
        <div className="px-6 py-[21px] bg-gradient-to-br from-white to-blue-100 border-b border-[#DAEBFF] flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">
            {t("modals.editStudent.title")}
          </h2>
          <button onClick={onClose} className="p-0.5" disabled={isSubmitting}>
            <img src={closeIcon} alt={t("modals.common.close")} className="w-[22px] h-[22px]" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-gray-600 text-sm font-medium">
              {t("modals.editStudent.fields.firstName")}
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t("modals.editStudent.placeholders.firstName")}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-gray-600 text-sm font-medium">
              {t("modals.editStudent.fields.lastName")}
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t("modals.editStudent.placeholders.lastName")}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-gray-600 text-sm font-medium">
              {t("modals.editStudent.fields.grade")}
            </label>
            <input
              type="text"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              placeholder={t("modals.editStudent.placeholders.grade")}
              className={inputClass}
            />
          </div>

          {/* Email is read-only — it's the student's login identity. */}
          <div className="flex flex-col gap-2">
            <label className="text-gray-600 text-sm font-medium">
              {t("modals.studentDetails.fields.email")}
            </label>
            <div className="h-11 px-3.5 bg-gray-50 rounded-[10px] border border-gray-200 flex items-center text-gray-500 text-base font-medium">
              {student?.email || t("manageStudents.na")}
            </div>
            <p className="text-gray-400 text-xs font-normal">
              {t("modals.editStudent.emailNote")}
            </p>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-2 mt-1">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-gray-900 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("modals.editStudent.buttons.cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-white rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isSubmitting
                ? t("modals.editStudent.buttons.saving")
                : t("modals.editStudent.buttons.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStudentModal;
